declare global {
	interface Window {
		focusTrap: { [key: string]: FocusTrap };
	}
}

interface FocusTrap {
	focusable: HTMLElement[];
	nodeFocusedBeforeActivation: Element;
	observer: MutationObserver;
	parentElement: Element;
}

const updateGlobalInterface = ({
	parentElement,
	focusable,
	observer,
}: {
	parentElement: Element;
	focusable: HTMLElement[];
	observer?: MutationObserver | null;
}) => {
	window.focusTrap[parentElement.id] = window.focusTrap[parentElement.id] || ({} as FocusTrap);
	window.focusTrap[parentElement.id].parentElement = parentElement;
	window.focusTrap[parentElement.id].focusable = focusable;

	if (observer) {
		window.focusTrap[parentElement.id].observer = observer;
	}
};

window.focusTrap = {};

export const getFocusableElements = (parentElement: HTMLElement): HTMLElement[] => {
	const focusableElements = 'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
	const elements = parentElement.querySelectorAll<HTMLElement>(focusableElements);
	const focusable: HTMLElement[] = [];

	[].forEach.call(elements, (el) => {
		focusable.push(el);
	});

	return focusable.filter((el: HTMLElement) => !(el as HTMLInputElement).disabled);
};

export type FocusTrapInstance = { start: (element: HTMLElement) => void; stop: () => void };

/**
 * Keep the focus inside of a element. For example if a user hits tab while a modal
 * is open, only cycle through the modal focusable elements.
 *
 * Uses MutationObserver API to track changes to the DOM regardless of js library.
 *
 * Returns two functions:
 * [
 *   "start the focus trap",
 *   "end the focus trap"
 * ]
 */
export const focusTrap = (parentElement: HTMLElement): FocusTrapInstance => {
	const focusable = getFocusableElements(parentElement);

	updateGlobalInterface({ parentElement, focusable });

	if (window.focusTrap[parentElement.id] && !window.focusTrap[parentElement.id].observer) {
		const observer = new MutationObserver(() => {
			updateGlobalInterface({
				parentElement,
				focusable: getFocusableElements(parentElement),
			});
		});

		observer.observe(parentElement, { attributes: true, childList: true, subtree: true });

		updateGlobalInterface({ parentElement, focusable, observer });
	}

	const trackTabAndShift = (e: KeyboardEvent) => {
		if ((e.key && e.key.toLowerCase() !== 'tab') || e.shiftKey) {
			return;
		}

		const focusableList = window.focusTrap[parentElement.id].focusable;
		const lastElement = focusableList[focusable.length - 1];
		const firstElement = focusableList[0];

		if (e.shiftKey) {
			if (document.activeElement === firstElement) {
				lastElement.focus();
				e.preventDefault();
			}
		} else {
			if (document.activeElement === lastElement) {
				firstElement.focus();
				e.preventDefault();
			}
		}
	};

	return {
		start: (focalPoint) => {
			document.addEventListener('keydown', trackTabAndShift);

			if (focalPoint) {
				const observer = new MutationObserver(() => {
					focalPoint.focus();
					observer.disconnect();
				});

				observer.observe(document.body, { attributes: false, childList: true, subtree: true });
			}
		},
		stop: () => {
			document.removeEventListener('keydown', trackTabAndShift);
			if (window.focusTrap[parentElement.id] && window.focusTrap[parentElement.id].observer) {
				window.focusTrap[parentElement.id].observer.disconnect();
			}
			delete window.focusTrap[parentElement.id];
		},
	};
};
